import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AppFooter from './AppFooter';
import AppHeader from './AppHeader';
import AppMain from './AppMain';
import { ReactComponent as StorageLogo } from './assets/img/home/header-storage.svg';
import { ReactComponent as At } from './assets/svg/At.svg';
import { ReactComponent as CatalogLogo } from './assets/svg/Catalog.svg';
import { ReactComponent as ShowcaseLogo } from './assets/svg/CatalogMin.svg';
import { ReactComponent as ConsumerLogo } from './assets/svg/Consumer.svg';
import { ReactComponent as NotificationLogo } from './assets/svg/Notify.svg';
import { ReactComponent as ReqLogo } from './assets/svg/PermRequest.svg';
import { ReactComponent as ToolsLogo } from './assets/svg/Tools.svg';
import { ROLE } from './components/common';
import { EMPTY_ROLE, PUBLIC_ROUTES, WELCOME_ROUTES } from './const';
import './css/common.css';
import { useAcc, useAuth, useBodyClass, useRole } from './hooks';
import './langs/i18n';
import { setRole, useLazyGetSummaryQuery } from './services/api';
import { useLazyGetCurrentAccountQuery, useLazyGetProfileQuery, useLazyGetRegistrationsQuery } from './services/users_v1';
import { CustomMenuItem } from './types/types';
import workerScript from './worker';

/**
 * Starts the page-change-detection worker.
 * The worker polls the page ETag and posts 'CHANGE' when a new build is detected.
 */
function startVersionWorker(): () => void {
    let worker: Worker | null = null;
    try {
        worker = new Worker(workerScript);
        worker.postMessage(document.baseURI);
        worker.onmessage = (e: MessageEvent) => {
            if (e.data === 'CHANGE') {
                const reload = window.confirm('The page version is out of date and needs to be reloaded');
                if (reload) {
                    window.location.href = `/?t=${new Date().getTime()}`;
                }
            }
        };
    } catch (err) {
        // Worker is a progressive enhancement – safe to ignore failures
        console.warn('Failed to start version-check worker:', err);
    }
    return () => {
        worker?.terminate();
    };
}

/**
 * Route helpers – keep the "is this a public/welcome/content-public path?"
 * logic in one place so it stays consistent across effects and render.
 */
const isWelcomeRoute = (path: string): boolean =>
    WELCOME_ROUTES.some((route) => path.startsWith(route));

const isPublicRoute = (path: string): boolean =>
    PUBLIC_ROUTES.some((route) => path.startsWith(route));

const isContentPublicRoute = (path: string): boolean =>
    path.startsWith('/content/view/')
    || path.startsWith('/content/public/')
    || path.startsWith('/product/public/')
    || path.startsWith('/content/service/view/');

/** Roles that render the "consumer" (subscriber) theme. */
const CONSUMER_ROLES: ROLE[] = [
    ROLE.CONSUMER_ADMIN,
    ROLE.CONSUMER_AUDITOR,
    ROLE.CONSUMER_CONTENT_MANAGER,
    ROLE.CONSUMER_ENGINEER,
    ROLE.CONSUMER_OWNER,
];

/** Roles that render the "moderator" theme. */
const MODERATOR_ROLES: ROLE[] = [
    ROLE.PARTNER_MODERATOR_ADMIN,
    ROLE.PARTNER_MODERATOR_MODERATOR,
    ROLE.PARTNER_MODERATOR_OWNER,
    ROLE.PARTNER_TVSRC_OWNER,
    ROLE.PARTNER_TVSRC_ADMIN,
    ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
    ROLE.SITE_MODERATOR,
    ROLE.PARTNER_TVSRC_MDLG_OWNER,
    ROLE.PARTNER_TVSRC_MDLG_ADMIN,
    ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
];

/**
 * Computes the body-class list for the current location/auth state.
 * Pure function – easy to test, no side effects.
 *
 * Theme rules:
 *  - white  – producer (provider/partner)
 *  - black  – consumer (subscriber)
 *  - yellow – site admin
 *  - blue   – moderator / partner
 */
function computeBodyClasses(params: {
    pathname: string;
    search: string;
    hasUser: boolean;
    hasAccount: boolean;
    roleLabel: ROLE;
}): string[] {
    const { pathname, search, hasUser, hasAccount, roleLabel } = params;
    const classes: string[] = [];

    const showWelcome = isWelcomeRoute(pathname) || (!hasUser && !isPublicRoute(pathname));
    const showView = search.includes('view=true');

    if (showWelcome) {
        classes.push('page-welcome', 'page-welcome-about');
    } else if (hasAccount) {
        const isConsumerRole = CONSUMER_ROLES.includes(roleLabel);
        if (!isConsumerRole && !isContentPublicRoute(pathname) && !showView) {
            classes.push('page-producer');
            if (roleLabel === ROLE.SITE_ADMIN) {
                classes.push('access-admin');
            } else if (MODERATOR_ROLES.includes(roleLabel)) {
                classes.push('access-mod');
            } else if (roleLabel === ROLE.GUEST_GUEST) {
                classes.push('access-guest');
            }
        } else {
            classes.push('page-subscriber');
        }
    } else if (hasUser) {
        classes.push('page-subscriber');
    } else {
        // Anonymous
        if (pathname.startsWith('/content/public/') || pathname.startsWith('/product/public/')) {
            classes.push('page-subscriber');
        } else {
            classes.push('page-home');
        }
    }

    if (pathname.startsWith('/sdn')) {
        classes.push('page-storage');
    }
    if (pathname.startsWith('/sdn-public')) {
        classes.push('page-producer', 'page-storage-guest');
    }

    return classes;
}

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const auth = useAuth();
    const acc = useAcc();
    const role = useRole();

    const [summaryPollingInterval, setSummaryPollingInterval] = useState(0);

    const [fetchProfile] = useLazyGetProfileQuery();
    const [fetchUserAccount] = useLazyGetCurrentAccountQuery();
    const [fetchSummary, { data: summary }] = useLazyGetSummaryQuery({ pollingInterval: summaryPollingInterval });
    const [fetchApproveRequests, { data: newUsers }] = useLazyGetRegistrationsQuery();

    const [bodyClasses, setBodyClasses] = useState<string[]>([]);

    // ------------------------------------------------------------------
    // One-time: stash server-rendered text into sessionStorage, then remove it
    // ------------------------------------------------------------------
    useEffect(() => {
        const text = document.getElementById('text');
        sessionStorage.text = text?.innerHTML || '';
        text?.remove();

        const text2 = document.getElementById('text2');
        sessionStorage.text2 = text2?.innerHTML || '';
        text2?.remove();
    }, []);

    // ------------------------------------------------------------------
    // Version-change worker (progressive enhancement)
    // ------------------------------------------------------------------
    useEffect(() => startVersionWorker(), []);

    // ------------------------------------------------------------------
    // Data fetching
    // ------------------------------------------------------------------
    // Fetch profile as soon as a user exists (even before account loads)
    useEffect(() => {
        if (auth.user && !auth.account) {
            fetchProfile(new Date().toISOString());
        }
    }, [auth.user, auth.account, fetchProfile]);

    // Once the account is known, start polling and load full account data
    useEffect(() => {
        if (auth.account) {
            fetchProfile(new Date().toISOString());
            fetchSummary();
            fetchUserAccount();
            setSummaryPollingInterval(15000);
        } else {
            setSummaryPollingInterval(0);
        }
    }, [auth.account, fetchProfile, fetchSummary, fetchUserAccount]);

    // Site admins see pending registrations
    useEffect(() => {
        if (auth.account && role.label === ROLE.SITE_ADMIN) {
            fetchApproveRequests();
        }
    }, [auth.account, role.label, fetchApproveRequests]);

    // ------------------------------------------------------------------
    // Body classes (global theming)
    // ------------------------------------------------------------------
    useEffect(() => {
        const classes = computeBodyClasses({
            pathname: location.pathname,
            search: location.search,
            hasUser: Boolean(auth.user),
            hasAccount: Boolean(auth.account),
            roleLabel: role.label,
        });
        setBodyClasses(classes);

        // Reset role when no one is logged in
        if (!auth.user) {
            dispatch(setRole(EMPTY_ROLE));
        }
    }, [location.pathname, location.search, auth.account, auth.user, dispatch, role.label]);

    useBodyClass(bodyClasses);

    // ------------------------------------------------------------------
    // Header fixed on scroll
    // ------------------------------------------------------------------
    const handleScroll = useCallback(() => {
        const root = document.documentElement;
        if (window.scrollY > 0) {
            root.classList.add('_header-fixed');
        } else {
            root.classList.remove('_header-fixed');
        }
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // ------------------------------------------------------------------
    // Konami-code easter eggs
    // ------------------------------------------------------------------
    const keyBuffer = useRef<string[]>([]);
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const buf = keyBuffer.current;
            buf.push(e.key);
            if (buf.length > 13) {
                buf.splice(0, buf.length - 13);
            }
            if (buf.join('') === 'doabarrelroll') {
                document.body.style = 'transition: transform 1s ease-in-out; transform: rotate(360deg);';
                setTimeout(() => {
                    document.body.style = '';
                }, 1000);
            } else if (buf.slice(-10).join('') === 'ArrowUpArrowUpArrowDownArrowDownArrowLeftArrowRightArrowLeftArrowRightba') {
                dispatch(setRole({ ...role, superAdmin: true }));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, role]);

    // ------------------------------------------------------------------
    // Navigation menu
    // ------------------------------------------------------------------
    const menuItems = useMemo<CustomMenuItem[]>(() => [
        {
            label: t('Showcase'),
            icon: <ShowcaseLogo />,
            command: () => navigate('/showcase'),
            link: '/showcase',
            avail: [
                ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_AUDITOR, ROLE.CONSUMER_CONTENT_MANAGER,
                ROLE.CONSUMER_ENGINEER, ROLE.CONSUMER_OWNER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ROLE.GUEST_GUEST,
            ],
        },
        {
            label: t('Registrations'),
            icon: <ReqLogo />,
            command: () => navigate('/requests'),
            data: newUsers?.results.length,
            link: '/requests',
            avail: [ROLE.SITE_ADMIN],
        },
        {
            label: t('Deals'),
            icon: <ReqLogo />,
            data: (summary?.results.access_requests.opened ?? 0) + (summary?.results.access_requests.pending ?? 0),
            command: () => navigate('/deals'),
            link: '/deals',
            noend: true,
            avail: [
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER,
                ROLE.SITE_ADMIN,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
            ],
        },
        {
            label: t('Notifications'),
            icon: <NotificationLogo />,
            command: () => navigate('/notifications'),
            data: summary?.results.notifictions.new,
            link: '/notifications',
            avail: [
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_AUDITOR,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_AUDITOR,
                ROLE.SITE_ADMIN, ROLE.SITE_MODERATOR,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
            ],
        },
        {
            label: t('Users'),
            icon: <ConsumerLogo />,
            command: () => navigate('/users'),
            link: '/users',
            avail: [ROLE.SITE_ADMIN],
        },
        {
            label: t('Accounts'),
            icon: <At />,
            command: () => navigate('/accounts'),
            link: '/accounts',
            avail: [ROLE.SITE_ADMIN],
        },
        {
            label: t('Tools'),
            icon: <ToolsLogo />,
            command: () => navigate('/tools'),
            link: '/tools',
            avail: [
                ROLE.SITE_ADMIN,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_STORAGE_OPERATOR,
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_STORAGE_OPERATOR,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ROLE.PARTNER_MODERATOR_OWNER, ROLE.PARTNER_MODERATOR_ADMIN,
                ROLE.PARTNER_OWNER, ROLE.PARTNER_ADMIN, ROLE.PARTNER_STORAGE_OPERATOR,
            ],
        },
        {
            label: t('storage:STORAGE'),
            icon: <StorageLogo className="header__nav-link-icon" />,
            command: () => navigate('/sdn'),
            link: '/sdn',
            noend: true,
            avail: [
                ROLE.SITE_ADMIN,
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_STORAGE_OPERATOR,
                ROLE.CONSUMER_OWNER, ROLE.CONSUMER_ADMIN, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_STORAGE_OPERATOR,
                ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_CONTENT_MANAGER,
                ROLE.PARTNER_TVSRC_MDLG_ADMIN, ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_CONTENT_MANAGER,
                ROLE.PARTNER_MODERATOR_OWNER, ROLE.PARTNER_MODERATOR_ADMIN,
                ROLE.PARTNER_OWNER, ROLE.PARTNER_ADMIN, ROLE.PARTNER_STORAGE_OPERATOR,
            ],
        },
        {
            label: t('Catalog'),
            icon: <CatalogLogo />,
            command: () => navigate('/content'),
            link: '/content',
            avail: [
                ROLE.PROVIDER_OWNER, ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_ENGINEER, ROLE.PROVIDER_AUDITOR, ROLE.PROVIDER_CONTENT_MANAGER,
                ROLE.SITE_ADMIN, ROLE.SITE_MODERATOR,
            ],
        },
    ], [navigate, newUsers?.results.length, summary, t]);

    const filteredMenuItems = useMemo<CustomMenuItem[]>(() => {
        if (!auth.account || role.label === ROLE.UNKNOWN) {
            return [];
        }
        return menuItems.filter((item) => {
            if (item.avail?.includes(ROLE.PARTICIPANT) && acc.hideParticipants) {
                return false;
            }
            return !item.avail || item.avail.includes(role.label);
        });
    }, [menuItems, auth.account, role.label, acc.hideParticipants]);

    // ------------------------------------------------------------------
    // Lazy CSS components (code-split)
    // ------------------------------------------------------------------
    const LazyStyleCssComponent = useMemo(() => lazy(() => import('./components/CssComponent')), []);
    const LazyWelcomeCssComponent = useMemo(() => lazy(() => import('./components/WelcomeCssComponent')), []);

    const showWelcome = isWelcomeRoute(location.pathname) || (!auth.user && !isPublicRoute(location.pathname));
    const showFooter = location.pathname === '/' || isWelcomeRoute(location.pathname);

    return (
        <>
            <Suspense fallback={null}>
                {showWelcome ? <LazyWelcomeCssComponent /> : <LazyStyleCssComponent />}
            </Suspense>
            <AppHeader items={filteredMenuItems} />
            <AppMain />
            {showFooter && <AppFooter />}
        </>
    );
}

export default App;
